// The app-wide record of work that would be lost by a reload.
//
// There was none before this: no beforeunload handler anywhere, and the
// isDirty flags that existed were local state in about eight pages. "Do not
// reload while someone is preparing payroll" therefore had nothing to ask.
//
// Two kinds, deliberately separate:
//   unsaved     — edited and not submitted. Reloading loses typing.
//   pending     — submitted and in flight. Reloading loses the ANSWER, and
//                 the request may still land, so a retry can double-charge.
// The second is the more dangerous and the UI says so differently.

export type HoldKind = 'unsaved' | 'pending';

export interface Hold {
  id: string;
  kind: HoldKind;
  /** What a person would lose, in their words: "payroll run for October". */
  label: string;
}

type Listener = () => void;

const holds = new Map<string, Hold>();
const listeners = new Set<Listener>();
const notify = () => listeners.forEach((l) => l());

/** Registers work in progress. Returns the release function; callers must
 *  call it on unmount or the hold leaks and blocks reloads forever. */
export const hold = (h: Hold): (() => void) => {
  holds.set(h.id, h);
  notify();
  return () => {
    if (holds.delete(h.id)) notify();
  };
};

export const currentHolds = (): Hold[] => [...holds.values()];

export const subscribeToHolds = (fn: Listener): (() => void) => {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
};

/** Test seam. Never call from app code. */
export const __resetHolds = () => { holds.clear(); notify(); };

export interface HoldSummary {
  unsaved: number;
  pending: number;
  total: number;
  /** The labels, deduplicated, for telling someone what is at stake. */
  labels: string[];
}

export const summarise = (list: Hold[]): HoldSummary => {
  const unsaved = list.filter((h) => h.kind === 'unsaved').length;
  const pending = list.filter((h) => h.kind === 'pending').length;
  return {
    unsaved,
    pending,
    total: list.length,
    labels: [...new Set(list.map((h) => h.label))],
  };
};

/** The sentence shown when a reload is refused. Pending work leads, because
 *  losing the answer to a submitted request is worse than losing typing. */
export const holdReason = (s: HoldSummary): string | null => {
  if (s.total === 0) return null;
  const what = s.labels.slice(0, 3).join(', ');
  const more = s.labels.length > 3 ? ` and ${s.labels.length - 3} more` : '';
  if (s.pending > 0) {
    return `Something is still being saved (${what}${more}). Reloading now could lose the result, or repeat it.`;
  }
  return `You have unsaved changes (${what}${more}). Reloading now would discard them.`;
};
